
import { useState, useEffect } from 'react';
import { isIOSInstallable, isDesktopChrome, debugPWAState } from '@/utils/pwaUtils';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOSInstallableState, setIsIOSInstallableState] = useState(false);
  const [isDesktopInstallable, setIsDesktopInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Debug PWA state on load
    debugPWAState();

    // Check if app is already installed
    const checkIfInstalled = () => {
      const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
      const isInWebAppMode = (window.navigator as any).standalone === true;
      const installed = isInStandaloneMode || isInWebAppMode;
      
      console.log('PWA Debug - Installation Check:', {
        isInStandaloneMode,
        isInWebAppMode,
        installed
      });
      
      setIsInstalled(installed);
    };

    // Check platform installability
    const checkPlatformInstallability = () => {
      const iosInstallable = isIOSInstallable();
      const desktopInstallable = isDesktopChrome();
      
      console.log('PWA Debug - Platform Installability:', {
        iosInstallable,
        desktopInstallable
      });
      
      setIsIOSInstallableState(iosInstallable);
      setIsDesktopInstallable(desktopInstallable);
    };

    checkIfInstalled();
    checkPlatformInstallability();

    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('PWA Debug - beforeinstallprompt event fired:', e);
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      console.log('PWA Debug - App was installed');
      setIsInstalled(true);
      setIsInstallable(false);
      setIsIOSInstallableState(false);
      setIsDesktopInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) {
      console.log('PWA Debug - No deferred prompt available');
      return false;
    }

    try {
      console.log('PWA Debug - Attempting to install app');
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      console.log('PWA Debug - Install prompt result:', outcome);
      
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsInstallable(false);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('PWA Debug - Error during installation:', error);
      return false;
    }
  };

  const dismissPrompt = () => {
    console.log('PWA Debug - Prompt dismissed');
    setIsInstallable(false);
    setIsIOSInstallableState(false);
    setIsDesktopInstallable(false);
    setDeferredPrompt(null);
  };

  const canShowPrompt = (isInstallable || isIOSInstallableState || isDesktopInstallable) && !isInstalled;
  
  console.log('PWA Debug - Final State:', {
    isInstallable,
    isIOSInstallableState,
    isDesktopInstallable,
    isInstalled,
    canShowPrompt,
    hasDeferredPrompt: !!deferredPrompt
  });

  return {
    isInstallable: canShowPrompt,
    isAndroidInstallable: isInstallable && !isInstalled,
    isIOSInstallable: isIOSInstallableState && !isInstalled,
    isDesktopInstallable: isDesktopInstallable && (isInstallable || isDesktopInstallable) && !isInstalled,
    isInstalled,
    installApp,
    dismissPrompt
  };
};
